import {
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { HeadTable, TableCaption } from 'components';
import { ButtonEdit, TableCellNoData } from 'components/common/table';
import PopupEditPurchaseHistory from 'components/Popups/PurchaseHistory/PopupEditPurchaseHistory';
import { convertTimeToLocal, getSortItem, printPrice } from 'helpers';
import { useDateTime } from 'hooks';
import {
  GET_CHILDREN_PARENT_DETAIL_TYPE_PARAMS_REQUEST,
  TYPE_POPUP,
} from 'models';
import { PURCHASE_HISTORY_ITEM_RESPONSE } from 'models/subscription';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setPurchaseHistoryListParams } from 'store/reducers/subscription';
import { getACPurchaseHistoryAction } from 'store/reducers/subscription/actionType';
import { purchaseHeadCells } from './helpers';

interface PurchaseHistoryProps {
  parentId: string;
}

const PurchaseHistory = ({ parentId }: PurchaseHistoryProps) => {
  const dispatch = useAppDispatch();
  const { purchaseHistoryList } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );
  const { params } = purchaseHistoryList;
  const { formatDateTime } = useDateTime();

  // State for edit popup
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<PURCHASE_HISTORY_ITEM_RESPONSE>();

  const onSetListParams = (
    params: GET_CHILDREN_PARENT_DETAIL_TYPE_PARAMS_REQUEST
  ) => {
    dispatch(
      setPurchaseHistoryListParams({
        ...params,
        parent_id: parentId,
        // has_balance: true,
      })
    );
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const { items } = usePagination({
    count: purchaseHistoryList.metadata.total_pages,
    page: purchaseHistoryList.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const sortItem = useMemo(() => {
    return getSortItem(params?.sort);
  }, [params]);

  const handleRequestSort = (
    _: any,
    property: keyof PURCHASE_HISTORY_ITEM_RESPONSE
  ) => {
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const setOpenEdit = (event: any, data: PURCHASE_HISTORY_ITEM_RESPONSE) => {
    event.stopPropagation();
    setSelectedItem(data);
    setOpenEditPopup(true);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.EDIT:
        setOpenEditPopup(false);
        setSelectedItem(undefined);
        break;
    }
  };

  const onReloadList = () => {
    if (params) {
      dispatch(
        getACPurchaseHistoryAction({
          ...params,
          parent_id: parentId,
          // has_balance: true,
        })
      );
    }
  };

  useEffect(() => {
    dispatch(
      getACPurchaseHistoryAction({
        ...params,
        parent_id: parentId,
        // has_balance: true,
      })
    );
  }, [params, dispatch, parentId]);

  return (
    <Stack spacing="8px">
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #D9D9D9',
          maxHeight: 'calc(100vh - 395px)',
        }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }} aria-labelledby="tableTitle">
          <HeadTable<PURCHASE_HISTORY_ITEM_RESPONSE>
            onCheckbox={false}
            onAction={true}
            order={sortItem?.order}
            orderBy={sortItem?.orderBy}
            onRequestSort={handleRequestSort}
            rowCount={purchaseHistoryList?.records.length}
            headCells={purchaseHeadCells}
          />
          <TableBody sx={{ height: 'auto !important' }}>
            {purchaseHistoryList.records?.filter(
              (item) => item.package !== null
            )?.length > 0 ? (
              purchaseHistoryList.records.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {row.package?.name}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {printPrice(row.parent_rate)}/hour
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {printPrice(row.package?.price)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {printPrice(row.balance)}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {formatDateTime(convertTimeToLocal(row.createdAt))}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 400,
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      {formatDateTime(convertTimeToLocal(row.expiredAt))}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCellNoData colSpan={purchaseHeadCells.length + 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TableCaption
        onlyOnePage={true}
        rowsPerPage={params?.limit ?? 0}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        metadata={purchaseHistoryList.metadata}
        numberItem={purchaseHistoryList.records.length}
        items={items}
      />
      {openEditPopup && (
        <PopupEditPurchaseHistory
          open={openEditPopup}
          subject={selectedItem}
          onSuccess={onReloadList}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.EDIT)}
        />
      )}
    </Stack>
  );
};

export default PurchaseHistory;
